import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, X, Play, Sparkles } from 'lucide-react';

// Inline keyframes for tour animations
const tourStyles = document.createElement('style');
tourStyles.textContent = `
  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes fadeInSlide {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
if (!document.getElementById('demo-tour-styles')) {
  tourStyles.id = 'demo-tour-styles';
  document.head.appendChild(tourStyles);
}

const TOUR_STEPS = [
  {
    id: 'welcome',
    page: '/employer/dashboard',
    title: 'Welcome to OnsiteHand',
    description: 'This guided tour will walk you through hiring a tradesperson — from searching to making an offer. Everything you see uses demo data, completely isolated from live users.',
    target: null,
    position: 'center',
    autoAction: null,
  },
  {
    id: 'select-trade',
    page: '/employer/dashboard',
    title: 'Step 1: Choose a Trade',
    description: 'Start by selecting the type of tradesperson you need. We\'ll search for a Painter.',
    target: '[data-testid="search-trade-select"]',
    position: 'bottom',
    autoAction: 'selectTrade',
  },
  {
    id: 'click-search',
    page: '/employer/dashboard',
    title: 'Step 2: Search',
    description: 'Hit Search to find available tradespeople in your area.',
    target: '[data-testid="search-btn"]',
    position: 'bottom',
    autoAction: 'clickSearch',
  },
  {
    id: 'browse-results',
    page: '/employer/dashboard',
    title: 'Step 3: Browse Results',
    description: 'Here are the matching tradespeople. Each card shows their trade, rating, and availability. Let\'s view the top result.',
    target: null,
    targetDynamic: 'firstResultCard',
    position: 'top',
    autoAction: null,
  },
  {
    id: 'view-profile',
    page: '/employer/dashboard',
    title: 'Step 4: Open Profile',
    description: 'Click "View Profile" to see their full details, reviews, and work history.',
    target: null,
    targetDynamic: 'firstViewProfileBtn',
    position: 'top',
    autoAction: 'clickViewProfile',
  },
  {
    id: 'profile-overview',
    page: '/profile/',
    title: 'Step 5: Profile Preview',
    description: 'You can see their trade, rating, day rate, and a preview of their bio. To see everything, you need to unlock the profile.',
    target: '[data-testid="profile-header"]',
    position: 'bottom',
  },
  {
    id: 'unlock-profile',
    page: '/profile/',
    title: 'Step 6: Unlock Profile',
    description: 'Unlocking costs 1 credit and reveals the full bio, all reviews, credentials, and complete work gallery.',
    target: '[data-testid="unlock-profile-btn"]',
    position: 'top',
    autoAction: 'clickUnlock',
  },
  {
    id: 'reviews-unlocked',
    page: '/profile/',
    title: 'Step 7: Full Profile Unlocked',
    description: 'You now have access to all their reviews and any verified credentials shown on their profile. This helps you make an informed hiring decision.',
    target: '[data-testid="reviews-section"]',
    position: 'top',
  },
  {
    id: 'start-chat',
    page: '/profile/',
    title: 'Step 8: Start a Conversation',
    description: 'Starting a chat costs 2 credits. Once started, all subsequent messages in this conversation are free.',
    target: '[data-testid="start-chat-btn"]',
    position: 'top',
    autoAction: 'clickStartChat',
  },
  {
    id: 'send-message',
    page: '/messages',
    title: 'Step 9: Send a Message',
    description: 'You\'re now in a direct conversation. Type your message and hit send. All messages after the first are free.',
    target: '[data-testid="message-input"]',
    position: 'top',
    autoAction: 'sendMessage',
  },
  {
    id: 'create-offer',
    page: '/messages',
    title: 'Step 10: Make an Offer',
    description: 'When you\'re ready to hire, click "Create Offer" to send a structured proposal with dates, rates, and terms.',
    target: '[data-testid="create-offer-btn"]',
    position: 'top',
    autoAction: null,
  },
  {
    id: 'complete',
    page: null,
    title: 'Tour Complete',
    description: 'You\'ve seen the full hiring flow — search, profile unlock, chat, and offer. This demo environment is completely isolated from live data. You can replay this tour anytime from the dashboard.',
    target: null,
    position: 'center',
    autoAction: null,
  },
];

// ─── localStorage helpers ───
const KEYS = {
  active: 'demo_tour_active',
  step: 'demo_tour_step',
  done: 'demo_tour_completed',
  tradeId: 'demo_tour_tradesperson_id',
};

function getTourState() {
  return {
    active: localStorage.getItem(KEYS.active) === 'true',
    step: parseInt(localStorage.getItem(KEYS.step) || '0', 10),
    done: localStorage.getItem(KEYS.done) === 'true',
    tradeId: localStorage.getItem(KEYS.tradeId) || '',
  };
}

function setTourState(updates) {
  Object.entries(updates).forEach(([k, v]) => {
    if (k in KEYS) localStorage.setItem(KEYS[k], String(v));
  });
}

function clearTour() {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
}

// ─── Exported utility for auto-start check ───
export function shouldAutoStartTour(user) {
  if (!user) return false;
  if (user.is_demo !== true) return false;
  if (user.user_type !== 'employer') return false;
  if (localStorage.getItem(KEYS.done) === 'true') return false;
  if (localStorage.getItem(KEYS.active) === 'true') return false;
  return true;
}

export async function startTour() {
  // Reset demo interactions for a fresh tour (credits, unlocks, chats)
  try {
    const { default: axios } = await import('axios');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    await axios.post(`${backendUrl}/api/demo/tour-reset`, {}, { withCredentials: true });
  } catch (e) {
    // Non-fatal: tour proceeds even if reset fails
  }
  setTourState({ active: true, step: 0 });
  localStorage.removeItem(KEYS.tradeId);
  window.dispatchEvent(new Event('demo-tour-start'));
}

// ─── Main Overlay Component ───
export default function DemoTourOverlay() {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const rafRef = useRef(null);
  const observerRef = useRef(null);

  // Sync from localStorage on mount and poll for changes
  const syncState = useCallback(() => {
    const s = getTourState();
    setActive(s.active);
    setStep(s.step);
  }, []);

  useEffect(() => {
    syncState();
    // Poll for tour state changes (handles cross-component communication)
    const interval = setInterval(syncState, 500);
    window.addEventListener('demo-tour-start', syncState);
    return () => {
      clearInterval(interval);
      window.removeEventListener('demo-tour-start', syncState);
    };
  }, [syncState]);

  // Current step definition
  const currentStep = TOUR_STEPS[step] || null;

  // Resolve target selector for dynamic targets
  const resolveTarget = useCallback(() => {
    if (!currentStep) return null;
    if (currentStep.target) return document.querySelector(currentStep.target);
    if (currentStep.targetDynamic === 'firstResultCard') {
      const cards = document.querySelectorAll('[data-testid^="view-profile-btn-"]');
      if (cards.length > 0) return cards[0].closest('.card') || cards[0].parentElement?.parentElement;
    }
    if (currentStep.targetDynamic === 'firstViewProfileBtn') {
      const btns = document.querySelectorAll('[data-testid^="view-profile-btn-"]');
      return btns[0] || null;
    }
    return null;
  }, [currentStep]);

  // Track the spotlight element position
  useEffect(() => {
    if (!active || !currentStep) { setSpotlightRect(null); return; }
    if (currentStep.position === 'center') { setSpotlightRect(null); return; }

    let tries = 0;
    const maxTries = 40; // 4 seconds

    const updateRect = () => {
      const el = resolveTarget();
      if (el) {
        // First scroll, then capture rect after scroll settles
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          const r = el.getBoundingClientRect();
          setSpotlightRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }, 400);
      } else if (tries < maxTries) {
        tries++;
        rafRef.current = setTimeout(updateRect, 100);
      } else {
        setSpotlightRect(null);
      }
    };

    rafRef.current = setTimeout(updateRect, 300);

    // Also observe DOM changes
    observerRef.current = new MutationObserver(() => {
      const el = resolveTarget();
      if (el) {
        const r = el.getBoundingClientRect();
        setSpotlightRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    });
    observerRef.current.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (rafRef.current) clearTimeout(rafRef.current);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [active, step, currentStep, resolveTarget, location.pathname]);

  // ─── Step navigation ───
  const goToStep = useCallback((newStep) => {
    if (newStep < 0 || newStep >= TOUR_STEPS.length) return;
    setTransitioning(true);
    setTourState({ step: newStep });
    setStep(newStep);

    const target = TOUR_STEPS[newStep];

    // Navigate if needed
    if (target.page && !location.pathname.startsWith(target.page)) {
      if (target.page === '/profile/') {
        const tid = localStorage.getItem(KEYS.tradeId);
        if (tid) navigate(`/profile/${tid}`);
      } else {
        navigate(target.page);
      }
    }

    setTimeout(() => setTransitioning(false), 400);
  }, [location.pathname, navigate]);

  const nextStep = useCallback(() => goToStep(step + 1), [step, goToStep]);
  const prevStep = useCallback(() => goToStep(step - 1), [step, goToStep]);

  const endTour = useCallback(() => {
    clearTour();
    localStorage.setItem(KEYS.done, 'true');
    setActive(false);
    setStep(0);
    setSpotlightRect(null);
    navigate('/employer/dashboard');
  }, [navigate]);

  // ─── Auto-actions ───
  const executeAutoAction = useCallback(async () => {
    if (!currentStep?.autoAction) return;

    const delay = (ms) => new Promise((r) => setTimeout(r, ms));

    switch (currentStep.autoAction) {
      case 'selectTrade': {
        const sel = document.querySelector('[data-testid="search-trade-select"]');
        if (sel) {
          const nativeSet = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set;
          nativeSet.call(sel, 'Painter');
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        await delay(600);
        nextStep();
        break;
      }
      case 'clickSearch': {
        const btn = document.querySelector('[data-testid="search-btn"]');
        if (btn) btn.click();
        await delay(2000);
        nextStep();
        break;
      }
      case 'clickViewProfile': {
        const btns = document.querySelectorAll('[data-testid^="view-profile-btn-"]');
        if (btns.length > 0) {
          const testid = btns[0].getAttribute('data-testid');
          const userId = testid.replace('view-profile-btn-', '');
          localStorage.setItem(KEYS.tradeId, userId);
          setTourState({ step: step + 1, tradeId: userId });
          setStep(step + 1);
          setTransitioning(true);
          navigate(`/profile/${userId}`);
          setTimeout(() => setTransitioning(false), 1500);
        } else {
          // No results — skip to next anyway
          nextStep();
        }
        break;
      }
      case 'clickUnlock': {
        const btn = document.querySelector('[data-testid="unlock-profile-btn"]');
        if (btn) {
          btn.click();
          await delay(2000);
        }
        // Always advance — profile may already be unlocked on replay
        nextStep();
        break;
      }
      case 'clickStartChat': {
        const nextIdx = step + 1;
        setTourState({ step: nextIdx });
        setStep(nextIdx);
        setTransitioning(true);
        
        const btn = document.querySelector('[data-testid="start-chat-btn"]');
        if (btn) {
          btn.click();
          await delay(3000);
        } else {
          // Chat already started — navigate to messages directly
          const tradeId = localStorage.getItem(KEYS.tradeId);
          if (tradeId) navigate(`/messages?user=${tradeId}`);
          await delay(2000);
        }
        
        // Send a message to ensure conversation exists
        const tradeId = localStorage.getItem(KEYS.tradeId);
        if (tradeId) {
          try {
            const { default: axios } = await import('axios');
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            await axios.post(`${backendUrl}/api/messages`, {
              recipient_id: tradeId,
              content: 'Hi, I saw your profile and I\'d like to discuss a painting job. Are you available next week?'
            }, { withCredentials: true });
          } catch (e) {
            // Non-fatal
          }
        }
        setTransitioning(false);
        break;
      }
      case 'sendMessage': {
        await delay(2000);
        const input = document.querySelector('[data-testid="message-input"]');
        if (input) {
          const nativeSet = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
          const msg = 'We need some interior painting done in our Battersea office. Could you give us a quote?';
          // Type character by character for effect
          for (let i = 0; i <= msg.length; i++) {
            nativeSet.call(input, msg.slice(0, i));
            input.dispatchEvent(new Event('input', { bubbles: true }));
            await delay(18);
          }
          await delay(600);
          const sendBtn = document.querySelector('[data-testid="send-btn"]');
          if (sendBtn) sendBtn.click();
          await delay(1500);
          nextStep();
        } else {
          // If no input (conversation not loaded), skip to next step
          await delay(500);
          nextStep();
        }
        break;
      }
      default:
        break;
    }
  }, [currentStep, step, nextStep, navigate]);

  // Don't render if not active
  if (!active || !currentStep) return null;

  // Check if we're on the right page for this step
  const onRightPage = !currentStep.page || location.pathname.startsWith(currentStep.page);
  if (!onRightPage && !transitioning) {
    // Navigate to correct page
    return null;
  }

  const isCenter = currentStep.position === 'center' || !spotlightRect;
  const isComplete = currentStep.id === 'complete';
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  // Tooltip positioning
  let tooltipStyle = {};
  if (!isCenter && spotlightRect) {
    const pad = 16;
    const tooltipW = 380;
    const tooltipH = 180;
    const centerX = Math.max(pad, Math.min(spotlightRect.left + spotlightRect.width / 2 - tooltipW / 2, window.innerWidth - tooltipW - pad));
    
    // Try preferred position; flip if it goes off-screen
    const spaceBelow = window.innerHeight - (spotlightRect.top + spotlightRect.height + pad);
    const spaceAbove = spotlightRect.top - pad;
    
    if (currentStep.position === 'bottom' && spaceBelow >= tooltipH) {
      tooltipStyle = { position: 'fixed', top: spotlightRect.top + spotlightRect.height + pad, left: centerX, width: tooltipW };
    } else if (spaceAbove >= tooltipH) {
      tooltipStyle = { position: 'fixed', top: spotlightRect.top - tooltipH - pad, left: centerX, width: tooltipW };
    } else {
      // Default: place below with viewport clamping
      tooltipStyle = { position: 'fixed', top: Math.min(spotlightRect.top + spotlightRect.height + pad, window.innerHeight - tooltipH - pad), left: centerX, width: tooltipW };
    }
  }

  return (
    <div
      data-testid="demo-tour-overlay"
      style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'auto' }}
    >
      {/* Backdrop with spotlight cutout */}
      {isCenter ? (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      ) : spotlightRect ? (
        <>
          {/* Dark overlay around spotlight - blocks clicks outside */}
          <div className="absolute inset-0" style={{ pointerEvents: 'auto' }} onClick={(e) => e.stopPropagation()} />
          <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
            <defs>
              <mask id="spotlight-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={spotlightRect.left - 8}
                  y={spotlightRect.top - 8}
                  width={spotlightRect.width + 16}
                  height={spotlightRect.height + 16}
                  rx="12"
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.6)"
              mask="url(#spotlight-mask)"
            />
            {/* Glow border around spotlight */}
            <rect
              x={spotlightRect.left - 8}
              y={spotlightRect.top - 8}
              width={spotlightRect.width + 16}
              height={spotlightRect.height + 16}
              rx="12"
              fill="none"
              stroke="rgba(249,115,22,0.6)"
              strokeWidth="3"
              className="animate-pulse"
            />
          </svg>
        </>
      ) : (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      )}

      {/* Tooltip / Card */}
      {isCenter ? (
        /* Centered modal */
        <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
            style={{ pointerEvents: 'auto', animation: 'fadeInScale 0.3s ease-out forwards' }}
            data-testid="demo-tour-card"
          >
            {/* Header bar */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Sparkles className="h-5 w-5" />
                  <span className="font-semibold text-sm">Demo Tour</span>
                </div>
                <button
                  onClick={endTour}
                  className="text-white/80 hover:text-white transition-colors"
                  data-testid="demo-tour-skip"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-3">{currentStep.title}</h2>
              <p className="text-slate-600 leading-relaxed">{currentStep.description}</p>
            </div>
            <div className="px-6 pb-6 flex items-center justify-between">
              {/* Progress */}
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>{step + 1}/{TOUR_STEPS.length}</span>
                <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <div className="flex gap-2">
                {!isFirst && !isComplete && (
                  <button onClick={prevStep} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-testid="demo-tour-prev">
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                )}
                {isComplete ? (
                  <button onClick={endTour} className="flex items-center gap-1 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold text-sm hover:from-orange-600 hover:to-amber-600 transition-all" data-testid="demo-tour-finish">
                    Finish Tour
                  </button>
                ) : (
                  <button onClick={currentStep.autoAction ? executeAutoAction : nextStep} className="flex items-center gap-1 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold text-sm hover:from-orange-600 hover:to-amber-600 transition-all" data-testid="demo-tour-next">
                    {currentStep.autoAction ? (
                      <><Play className="h-4 w-4" /> Go</>
                    ) : (
                      <>Next <ChevronRight className="h-4 w-4" /></>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Positioned tooltip */
        <div style={{ ...tooltipStyle, pointerEvents: 'auto', zIndex: 100000 }} data-testid="demo-tour-card">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" style={{ animation: 'fadeInSlide 0.2s ease-out forwards' }}>
            {/* Orange top accent */}
            <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-500" />
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-slate-900 text-sm">{currentStep.title}</h3>
                <button onClick={endTour} className="text-slate-400 hover:text-slate-600 ml-2 shrink-0" data-testid="demo-tour-skip">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">{currentStep.description}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>{step + 1}/{TOUR_STEPS.length}</span>
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <div className="flex gap-2">
                  {!isFirst && (
                    <button onClick={prevStep} className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-testid="demo-tour-prev">
                      <ChevronLeft className="h-3 w-3" /> Back
                    </button>
                  )}
                  <button
                    onClick={currentStep.autoAction ? executeAutoAction : nextStep}
                    className="flex items-center gap-1 px-4 py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold text-xs hover:from-orange-600 hover:to-amber-600 transition-all"
                    data-testid="demo-tour-next"
                  >
                    {currentStep.autoAction ? (
                      <><Play className="h-3 w-3" /> Go</>
                    ) : (
                      <>Next <ChevronRight className="h-3 w-3" /></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
